/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SolutionSpace } from 'src/app/models/solution';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { NgStyle } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { ComponentTypeService } from 'src/app/services/api/componentType.service';
import { GeofenceService } from 'src/app/services/api/geofence.service';
import { Geofence } from 'src/app/models/geofence';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { SelectorDialogComponent } from 'src/app/shared/selector-dialog/selector-dialog.component';

export interface ComponentTypeDiaglog {
  id?: string;
  solution: SolutionSpace;
  name?: string;
  description?: string;
  distanceRangeLow?: number;
  distanceRangeHigh?: number;
  isEdit: boolean;
  humidityThreshold?: number;
  isHumidityWarningActive: boolean;
  shockThreshold?: number;
  isShockWarningActive: boolean;
  serviceLocationsToAdd?: string[];
  serviceLocationsToRemove?: string[];
}

@Component({
  selector: 'app-type-dialog',
  templateUrl: './type-dialog.component.html',
  styleUrls: ['./type-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    AngularSvgIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatButtonModule,
    NgStyle,
    MatIconModule,
    MatListModule,
  ],
})
export class TypeDialogComponent implements OnInit {
  createClicked = false;
  deleteClicked = false;
  shockValues: number[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
  componentTypeForm = this.fb.group({
    name: [this.data.name, Validators.required],
    description: [this.data.description],
    mindistance: [this.data.distanceRangeLow?.toString(), Validators.required],
    maxdistance: [this.data.distanceRangeHigh?.toString(), Validators.required],
    humidityThreshold: [this.data.humidityThreshold?.toString()],
    isHumidityWarningActive: [this.data.isHumidityWarningActive],
    shockThreshold: [this.data.shockThreshold?.toString()],
    isShockWarningActive: [this.data.isShockWarningActive],
  });
  min = 130;
  max = 800;
  interval = 10;
  solution = this.data.solution;
  serviceLocations: Geofence[] = [];
  originalServiceLocations: Geofence[] = [];
  allGeofences: Geofence[] = [];
  isLoadingLocations = false;

  get serviceLocationsChanged(): boolean {
    const { toAdd, toRemove } = this.getServiceLocationChanges();
    return toAdd.length > 0 || toRemove.length > 0;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ComponentTypeDiaglog,
    private fb: FormBuilder,
    private componentTypeService: ComponentTypeService,
    private geofenceService: GeofenceService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService
  ) {}

  get minValues(): number[] {
    const v = [];
    for (let i = this.min; i <= this.max - this.interval; i += this.interval) {
      v.push(i);
    }
    return v;
  }

  get maxValues(): number[] {
    const v = [];
    for (let i = this.min + this.interval; i <= this.max; i += this.interval) {
      v.push(i);
    }
    return v;
  }

  ngOnInit(): void {
    // Load service locations for utilization component types when editing
    if (this.isUtilization && this.data.isEdit && this.data.id) {
      this.loadServiceLocations();
    }

    if (this.isUtilization) {
      this.loadAllGeofences();
    }
  }

  invalidForm(): boolean {
    let invalid = this.componentTypeForm.controls['name'].invalid;
    if (!this.isHumidity) {
      invalid =
        invalid ||
        this.componentTypeForm.controls['maxdistance'].invalid ||
        this.componentTypeForm.controls['mindistance'].invalid;
    }
    return invalid;
  }

  hasChanges(): boolean {
    return this.componentTypeForm.dirty || this.serviceLocationsChanged;
  }

  updateDisabled(): boolean {
    return this.invalidForm() || !this.hasChanges();
  }

  get minVal(): number {
    return parseInt(this.componentTypeForm.get('mindistance')?.value as string, 10);
  }

  get maxVal(): number {
    return parseInt(this.componentTypeForm.get('maxdistance')?.value as string, 10);
  }

  minChange(): void {
    const minIndex = this.minValues.indexOf(this.minVal);
    const maxIndex = this.maxValues.indexOf(this.maxVal);
    if (isNaN(this.maxVal) || minIndex > maxIndex) {
      this.componentTypeForm.get('maxdistance')?.setValue(this.maxValues[minIndex].toString());
    }
  }

  maxChange(): void {
    const minIndex = this.minValues.indexOf(this.minVal);
    const maxIndex = this.maxValues.indexOf(this.maxVal);
    if (isNaN(this.minVal) || minIndex > maxIndex) {
      this.componentTypeForm.get('mindistance')?.setValue(this.minValues[maxIndex].toString());
    }
  }

  onClick(): void {
    if (this.createClicked) return;
    this.createClicked = true;
    this.updateDialogDatawithFormValue();

    if (this.isUtilization) {
      const changes = this.getServiceLocationChanges();
      this.data.serviceLocationsToAdd = changes.toAdd;
      this.data.serviceLocationsToRemove = changes.toRemove;
    }
  }

  private updateDialogDatawithFormValue() {
    this.data.name = this.componentTypeForm.value['name'] ?? '';

    if (this.componentTypeForm.value['mindistance'] !== null && this.componentTypeForm.value['mindistance'] !== null) {
      this.data.distanceRangeLow = parseInt(this.componentTypeForm.value['mindistance'] ?? '', 10);
      this.data.distanceRangeHigh = parseInt(this.componentTypeForm.value['maxdistance'] ?? '', 10);
    }
    this.data.description = this.componentTypeForm.value['description'] ?? undefined;

    if (this.componentTypeForm.value['humidityThreshold'] !== null) {
      this.data.humidityThreshold = parseInt(this.componentTypeForm.value['humidityThreshold'] ?? '', 10);
    }

    this.data.isHumidityWarningActive = this.componentTypeForm.value['isHumidityWarningActive'] ?? false;

    if (this.componentTypeForm.value['shockThreshold'] !== null) {
      this.data.shockThreshold = parseInt(this.componentTypeForm.value['shockThreshold'] ?? '', 10);
    }

    this.data.isShockWarningActive = this.componentTypeForm.value['isShockWarningActive'] ?? false;
  }

  deleteDisabled(): boolean {
    return false;
  }

  get isHumidity(): boolean {
    return this.data.solution === SolutionSpace.Humidity;
  }

  get isUtilization(): boolean {
    return this.data.solution === SolutionSpace.Utilization;
  }

  get shockWarningValue(): number {
    return parseInt(this.componentTypeForm.get('shockThreshold')?.value as string, 10);
  }

  onShockValueChange(): void {
    this.componentTypeForm.get('shockThreshold')?.setValue(this.shockWarningValue.toString());
  }

  loadServiceLocations(): void {
    if (!this.data.id) return;

    this.isLoadingLocations = true;
    this.componentTypeService.getGeofencesForComponentType(this.data.id).subscribe({
      next: (geofences) => {
        this.serviceLocations = [...geofences];
        this.originalServiceLocations = [...geofences];
        this.isLoadingLocations = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading service locations:', error);
        this.snackbarService.openSnackBar({ title: 'Error', message: 'Failed to load service locations' }, 'ERROR');
        this.isLoadingLocations = false;
      },
    });
  }

  loadAllGeofences(): void {
    this.geofenceService.getGeofences({ pageNumber: 1, pageSize: 1000 }).subscribe({
      next: (response) => {
        this.allGeofences = response.items;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading geofences:', error);
      },
    });
  }

  addServiceLocation(): void {
    if (this.allGeofences.length === 0) {
      this.snackbarService.openSnackBar(
        { title: 'Info', message: 'No geofence available for creating service locations' },
        'UPDATE'
      );
      return;
    }

    const availableGeofences = this.allGeofences.filter(
      (geofence) => !this.serviceLocations.some((sl) => sl.id === geofence.id)
    );

    if (availableGeofences.length === 0) {
      this.snackbarService.openSnackBar(
        { title: 'Info', message: 'All geofences are already added as service locations' },
        'UPDATE'
      );
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '500px';
    dialogConfig.data = {
      title: 'Select Geofence As Service Location',
      label: 'Service Location',
      items: availableGeofences,
      displayFn: (geofence: Geofence) => {
        return geofence.description ? `${geofence.name} - ${geofence.description}` : geofence.name;
      },
      primaryButtonText: 'Add',
    };

    const dialogRef = this.dialog.open(SelectorDialogComponent<Geofence>, dialogConfig);

    dialogRef.afterClosed().subscribe((selectedGeofences: Geofence[] | undefined) => {
      if (selectedGeofences && selectedGeofences.length > 0) {
        this.serviceLocations = [...this.serviceLocations, ...selectedGeofences];
      }
    });
  }

  removeServiceLocation(geofence: Geofence): void {
    this.serviceLocations = this.serviceLocations.filter((loc) => loc.id !== geofence.id);
  }

  getServiceLocationChanges(): { toAdd: string[]; toRemove: string[] } {
    const originalIds = this.originalServiceLocations.map((loc) => loc.id);
    const currentIds = this.serviceLocations.map((loc) => loc.id);

    const toAdd = currentIds.filter((id) => !originalIds.includes(id));
    const toRemove = originalIds.filter((id) => !currentIds.includes(id));

    return { toAdd, toRemove };
  }
}
